import { useCallback } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useMutation } from 'react-relay';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagsService } from '@atlassian/jira-flags';
import type { ReorderRow } from '@atlassian/jira-native-issue-table/src/common/types.tsx';
import type {
	useIssuesRanker_rankIssuesMutation,
	useIssuesRanker_rankIssuesMutation$data,
	JiraRankMutationEdge,
} from '@atlassian/jira-relay/src/__generated__/useIssuesRanker_rankIssuesMutation.graphql';
import messages from '../../common/flags/messages.tsx';

const getRankMutationEdge = (edge: Edge): JiraRankMutationEdge | null => {
	switch (edge) {
		case 'top':
			return 'TOP';
		case 'bottom':
			return 'BOTTOM';
		default:
			// Unsupported edge
			return null;
	}
};

/**
 * @deprecated Use built-in `useIssueRanker` in NativeIssueTable
 * TODO: Remove on jsc-2065-unify-onrowreorder cleanup
 */
export const useIssuesRanker = (issueConnectionId: string) => {
	const { showFlag } = useFlagsService();

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [rankIssuesMutation] = useMutation<useIssuesRanker_rankIssuesMutation>(graphql`
		mutation useIssuesRanker_rankIssuesMutation($rankInput: JiraRankMutationInput!) {
			jira {
				rankIssues(rankInput: $rankInput) {
					success
				}
			}
		}
	`);

	const showErrorFlag = useCallback(() => {
		showFlag({
			type: 'error',
			title: fg('jira-issue-terminology-refresh-m3')
				? messages.reorderErrorTitleIssueTermRefresh
				: messages.reorderErrorTitle,
			description: messages.reorderErrorDescription,
			isAutoDismiss: true,
			messageId: 'issue-view-child-issues-panel.services.use-issues-ranker.show-flag.error',
			messageType: 'transactional',
		});
	}, [showFlag]);

	const reorderIssuesUpdater = useCallback(
		(store: RecordSourceSelectorProxy, startIndex: number, finishIndex: number) => {
			const issueConnection = store.get(issueConnectionId);
			if (!issueConnection) return;

			const edges = issueConnection.getLinkedRecords('edges');
			if (!edges) return;

			const reorderedEdges = reorder({
				list: edges,
				startIndex,
				finishIndex,
			});

			issueConnection.setLinkedRecords(reorderedEdges, 'edges');
		},
		[issueConnectionId],
	);

	const rankIssues = useCallback<ReorderRow>(
		(payload) => {
			const { source, destination, edge } = payload;

			const rankMutationEdge = getRankMutationEdge(edge);

			if (rankMutationEdge == null) {
				showErrorFlag();
				fireErrorAnalytics({
					meta: {
						id: 'commitRankIssuesError',
						packageName: 'jiraIssueViewCommonViews',
						teamName: 'bento',
					},
					error: new Error(`Edge "${edge}" is unsupported in JiraMutation.rankIssues`),
					sendToPrivacyUnsafeSplunk: true,
				});

				return;
			}

			rankIssuesMutation({
				variables: {
					rankInput: {
						issues: source.issueIds,
						relativeToIssue: destination.relativeToIssueId,
						edge: rankMutationEdge,
					},
				},
				optimisticUpdater(store) {
					reorderIssuesUpdater(store, source.rowIndex, destination.rowIndex);
				},
				updater(store, data) {
					// Rollback update if mutation fails
					if (!data?.jira?.rankIssues?.success) return;

					reorderIssuesUpdater(store, source.rowIndex, destination.rowIndex);
				},
				onCompleted(data: useIssuesRanker_rankIssuesMutation$data) {
					if (!data.jira?.rankIssues?.success) {
						showErrorFlag();
						fireErrorAnalytics({
							meta: {
								id: 'commitRankIssuesMutationUnsuccessful',
								packageName: 'jiraIssueViewCommonViews',
								teamName: 'bento',
							},
							sendToPrivacyUnsafeSplunk: true,
						});
					}
				},
				onError(error: Error) {
					showErrorFlag();
					fireErrorAnalytics({
						meta: {
							id: 'commitRankIssuesMutationError',
							packageName: 'jiraIssueViewCommonViews',
							teamName: 'bento',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				},
			});
		},
		[rankIssuesMutation, reorderIssuesUpdater, showErrorFlag],
	);

	return rankIssues;
};
